/**
 * Revision Workflow Hook
 * Manages feedback and revision logic for the Athena TARA workflow
 */

import { useState, useCallback, useRef } from 'react';
import { WORKFLOW_TIMINGS } from '../../../config/athenaWorkflowConfig';
import { getAgentName } from '../../../config/agentMapping';
import { reviseWorkflow } from '../../../api/workflow';

export const useRevisionWorkflow = (backendService) => {
  const [isRevising, setIsRevising] = useState(false);
  const [revisionStatus, setRevisionStatus] = useState('idle');
  const [currentRevision, setCurrentRevision] = useState(null);
  const [revisionHistory, setRevisionHistory] = useState([]);
  const [error, setError] = useState(null);

  const revisionTimeoutRef = useRef(null);
  const abortControllerRef = useRef(null);

  /**
   * Clean up revision operations
   */
  const cleanup = useCallback(() => {
    if (revisionTimeoutRef.current) {
      clearTimeout(revisionTimeoutRef.current);
      revisionTimeoutRef.current = null;
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, []);

  /**
   * Generate feedback based on context
   */
  const generateFeedback = useCallback((context) => {
    switch (context.type) {
      case 'completion_approve':
        return 'next';

      case 'user_feedback':
        return context.userInput || context.feedback || 'Please rerun with user feedback';

      case 'needs_rerun':
        return `Please rerun ${context.agentName} - user requested retry`;

      case 'auto_retry':
        return `Please rerun ${context.agentName} - automatic retry attempt ${context.attempt || 1}`;

      case 'rate_limit_retry':
        return `Please rerun ${context.agentName} - rate limit retry after cooldown`;

      default:
        if (typeof context === 'string') {
          return context; // Backward compatibility
        }
        return `Please rerun ${context.agentName || 'the current agent'}`;
    }
  }, []);

  /**
   * Submit revision with feedback
   */
  const submitRevision = useCallback(async (step, feedback, agentName = null) => {
    if (!backendService?.current?.threadId) {
      const error = new Error('No active workflow session');
      setError(error);
      throw error;
    }

    // Determine agent name if not provided
    const targetAgent = agentName || getAgentName(step, 'primary');

    if (!targetAgent) {
      const error = new Error(`Invalid agent for step ${step}`);
      setError(error);
      throw error;
    }

    console.log(`Submitting revision for ${targetAgent} with feedback:`, feedback);

    // Set revision state
    setIsRevising(true);
    setRevisionStatus('submitting');
    setError(null);

    // Create abort controller
    cleanup();
    abortControllerRef.current = new AbortController();

    // Track revision
    const revisionData = {
      id: `revision_${Date.now()}`,
      step,
      agentName: targetAgent,
      feedback,
      submittedAt: Date.now(),
      status: 'pending'
    };

    setCurrentRevision(revisionData);

    try {
      // Set revision flag on backend service
      if (backendService.current) {
        backendService.current.isRevisionInProgress = true;
      }

      // Submit revision to backend
      let result;
      try {
        result = await reviseWorkflow({
          baseURL: backendService.current.baseURL,
          threadId: backendService.current.threadId,
          feedback,
          targetAgent,
          signal: abortControllerRef.current.signal,
        });
      } catch (err) {
        const status = err?.status ?? 'unknown';
        const detail = err?.message || err?.detail || '';
        throw new Error(`Revision failed: ${status} - ${detail}`);
      }
      console.log('Revision submitted successfully:', result);

      // Update revision status
      setRevisionStatus('processing');
      revisionData.status = 'processing';
      revisionData.response = result;
      setCurrentRevision({ ...revisionData });

      // Poll for agent completion to get updated results
      console.log(`Polling for ${targetAgent} completion after revision...`);
      const pollResult = await backendService.current.pollForAgentCompletion(
        targetAgent,
        false,
        abortControllerRef.current?.signal
      );
      console.log('Revision poll result:', pollResult);

      // Clear revision flag
      if (backendService.current) {
        backendService.current.isRevisionInProgress = false;
      }

      // Update revision as completed
      revisionData.status = 'completed';
      revisionData.completedAt = Date.now();
      revisionData.result = pollResult;
      setCurrentRevision({ ...revisionData });

      // Add to history
      setRevisionHistory(prev => [...prev, revisionData]);

      setRevisionStatus('completed');
      setIsRevising(false);

      return {
        success: true,
        agentName: targetAgent,
        revisionId: revisionData.id,
        result: pollResult
      };

    } catch (error) {
      console.error('Revision submission failed:', error);

      // Clear revision flag
      if (backendService.current) {
        backendService.current.isRevisionInProgress = false;
      }

      // Update revision as failed
      revisionData.status = 'failed';
      revisionData.error = error.message;
      revisionData.failedAt = Date.now();
      setCurrentRevision({ ...revisionData });

      // Add to history
      setRevisionHistory(prev => [...prev, revisionData]);

      setRevisionStatus('failed');
      setIsRevising(false);
      setError(error);

      throw error;
    } finally {
      cleanup();
    }
  }, [backendService, cleanup, generateFeedback]);

  /**
   * Retry a revision after failure
   */
  const retryRevision = useCallback(async () => {
    if (!currentRevision || currentRevision.status !== 'failed') {
      console.warn('No failed revision to retry');
      return null;
    }

    console.log('Retrying revision:', currentRevision);

    return submitRevision(
      currentRevision.step,
      currentRevision.feedback,
      currentRevision.agentName
    );
  }, [currentRevision, submitRevision]);

  /**
   * Cancel ongoing revision
   */
  const cancelRevision = useCallback(() => {
    if (!isRevising) {
      console.warn('No revision in progress');
      return false;
    }

    console.log('Cancelling revision');
    cleanup();

    // Update current revision as cancelled
    if (currentRevision) {
      currentRevision.status = 'cancelled';
      currentRevision.cancelledAt = Date.now();
      setRevisionHistory(prev => [...prev, currentRevision]);
    }

    // Clear revision flag on backend service
    if (backendService?.current) {
      backendService.current.isRevisionInProgress = false;
    }

    setIsRevising(false);
    setRevisionStatus('cancelled');
    setCurrentRevision(null);

    return true;
  }, [isRevising, currentRevision, backendService, cleanup]);

  /**
   * Check if a specific step has revisions
   */
  const hasRevisions = useCallback((step) => {
    return revisionHistory.some(rev => rev.step === step);
  }, [revisionHistory]);

  /**
   * Get revisions for a specific step
   */
  const getStepRevisions = useCallback((step) => {
    return revisionHistory.filter(rev => rev.step === step);
  }, [revisionHistory]);

  /**
   * Get the latest revision for a step
   */
  const getLatestRevision = useCallback((step) => {
    const stepRevisions = getStepRevisions(step);
    return stepRevisions.length > 0 ? stepRevisions[stepRevisions.length - 1] : null;
  }, [getStepRevisions]);

  /**
   * Clear revision history
   */
  const clearHistory = useCallback(() => {
    setRevisionHistory([]);
    setCurrentRevision(null);
    console.log('Revision history cleared');
  }, []);

  /**
   * Get revision statistics
   */
  const getStatistics = useCallback(() => {
    const total = revisionHistory.length;
    const successful = revisionHistory.filter(r => r.status === 'completed').length;
    const failed = revisionHistory.filter(r => r.status === 'failed').length;
    const cancelled = revisionHistory.filter(r => r.status === 'cancelled').length;

    const byStep = {};
    revisionHistory.forEach(rev => {
      if (!byStep[rev.step]) {
        byStep[rev.step] = 0;
      }
      byStep[rev.step]++;
    });

    const averageTime = revisionHistory
      .filter(r => r.status === 'completed' && r.completedAt && r.submittedAt)
      .reduce((acc, r, _, arr) => {
        const time = r.completedAt - r.submittedAt;
        return acc + time / arr.length;
      }, 0);

    return {
      total,
      successful,
      failed,
      cancelled,
      byStep,
      averageTime: Math.round(averageTime),
      successRate: total > 0 ? (successful / total) * 100 : 0
    };
  }, [revisionHistory]);

  /**
   * Restore revision state from saved data
   */
  const restoreState = useCallback((savedState) => {
    if (savedState) {
      setRevisionHistory(savedState.revisionHistory || []);
      setCurrentRevision(savedState.currentRevision || null);
      setIsRevising(savedState.isRevising || false);
      setRevisionStatus(savedState.revisionStatus || 'idle');
      console.log('Revision state restored', savedState);
    }
  }, []);

  /**
   * Get current state for persistence
   */
  const getState = useCallback(() => {
    return {
      revisionHistory,
      currentRevision,
      isRevising,
      revisionStatus
    };
  }, [revisionHistory, currentRevision, isRevising, revisionStatus]);

  return {
    // State
    isRevising,
    revisionStatus,
    currentRevision,
    revisionHistory,
    error,

    // Actions
    submitRevision,
    retryRevision,
    cancelRevision,

    // Queries
    hasRevisions,
    getStepRevisions,
    getLatestRevision,
    getStatistics,

    // State management
    clearHistory,
    restoreState,
    getState,

    // Utilities
    generateFeedback
  };
};